import {AxiosError, AxiosResponse} from 'axios';
import FileDownload from 'js-file-download';
import {Dispatch, SetStateAction} from 'react';

import {IDocumentsUploadConfig} from 'components/documents/file-modal/hooks';
import {TFileInfo} from 'modules/data';
import {ILookupEntry} from 'shared/types/lookups';
import {ERequestType} from 'shared/types/requests';
import {
    convertToFormData, performRequest, TRequestPromise, showMessageFromResponse,
} from 'shared/utils';
import {TJsonFormValue} from 'shared/utils/convert-to-form-data';
import {compressFile} from 'shared/utils/file';

import {
    IDocumentFileInfo,
    EDocumentsSigningMethod,
    IDocumentTemplatesTypeResponse,
    IDocumentTypeResponse,
    IDocumentUploadFile,
    IDocumentUploadFileResponse,
    IDocumentUploadSignatureResponse,
    IFileAttachInfoResponse,
    EFileSizeFormat,
    IFileValidationResponse,
    IDocumentUploadFileRequest,
} from '../model/types';

// ==== API to work with uploading files ====
export const fetchDocumentsForUploadAttach = async (
    {sublistCode}: IFetchDocumentsForUploadAttachArgs,
) => performRequest<ILookupEntry[]>({
    url: 'valueLists/DOCUMENTS_FOR_UPLOAD_ATTACH',
    method: ERequestType.GET,
    params: {
        sublistCode,
    },
});

export const downloadDocumentScan = async (
    {attachmentId, fileName = 'scan'}: IDownloadDocumentScanArgs,
) => {
    const fileResponse = await performRequest({
        url: `download-attached/${attachmentId}`,
        responseType: 'blob',
        method: ERequestType.POST,
    });

    FileDownload(fileResponse.data, fileName);

    return fileResponse;
};

export const downloadScansArchiveByDocumentId = async (
    {docId, fileName = 'archive.zip'}: IDownloadScansArchiveByDocumentIdArgs,
) => {
    const fileResponse = await performRequest({
        url: `download-zip-attached/${docId}`,
        responseType: 'blob',
        method: ERequestType.POST,
    });

    FileDownload(fileResponse.data, fileName);

    return fileResponse;
};

export const fetchDocumentScansById = (
    {docId}: IFetchDocumentScansByIdArgs,
) => performRequest<TFileInfo[]>({
    url: `scan-attached/${docId}`,
});

export const deleteDocumentScan = ({
    attachmentId,
}: IDeleteDocumentScanArgs) => performRequest<string>({
    url: `delete-attached/${attachmentId}`,
    headers: {
        'Content-Type': 'application/json',
    },
    method: ERequestType.DELETE,
});

export const deleteDocumentScansByDocumentId = ({
    docId,
}: IDeleteDocumentScansByDocumentIdArgs) => performRequest<string>({
    url: `delete-all-attached/${docId}`,
    method: ERequestType.DELETE,
});

export const uploadAttachedToDocument = ({
    docId,
    file,
    handleProgress,
}: IUploadAttachedArgs) => performRequest((() => {
    const formData = new FormData();

    formData.append('file', file);

    return {
        url: `upload-attached/${docId}`,
        data: formData,
        method: ERequestType.POST,
        onUploadProgress: progressEvent => {
            handleProgress?.(progressEvent);
        },
    };
})())
    .catch((error: AxiosError) => {
        if (error?.response?.status === 422) {
            showMessageFromResponse({response: error.response, isError: true});
        }
        throw error;
    });

export const fetchDocumentTypesOnUpload = (): TRequestPromise<IDocumentTypeResponse[]> => performRequest({
    method: ERequestType.GET,
    url: 'valueLists/DOCUMENT_TYPE_ON_UPLOAD',
});

export const fetchDocumentTypes = (): TRequestPromise<IDocumentTypeResponse[]> => performRequest({
    method: ERequestType.GET,
    url: 'lookupValue/DOCUMENT_TYPE',
});

export const fetchDocumentInfo = (
    {attachId, data}: {attachId?: string; data?: IDocumentInfoParams},
): TRequestPromise<IDocumentUploadSignatureResponse[]> => {
    const formData = convertToFormData(data as Record<string, TJsonFormValue>);

    return performRequest({
        method: ERequestType.POST,
        url: `fileupload/sign-attach-info/${attachId}`,
        data: formData,
    });
};

export const fetchDocumentAttachInfo = (
    {attachId}: {attachId: string},
): TRequestPromise<IDocumentFileInfo> => performRequest({
    method: ERequestType.GET,
    url: `upload-attached-other/${attachId}`,
});

export const fetchBranchesLinkingFlag = (): TRequestPromise<string> => performRequest({
    method: ERequestType.GET,
    url: 'property/LINKING_DOCUMENTS_TO_BRANCHES_WHEN_UPLOADED',
});

export const fetchAcceptableExtensions = (): TRequestPromise<string> => performRequest({
    method: ERequestType.GET,
    url: '/property/SIGN_AVAIL_EXTENSIONS',
});

export const fetchDocumentsForSign = (
    contextParam: IDocumentForSignType,
): TRequestPromise<IDocumentTypeResponse[]> => performRequest({
    method: ERequestType.POST,
    url: '/fileupload/documentAttachments',
    data: contextParam,
});

export const fetchDefaultFileInfo = (
    sectionCode: string,
    subsectionCode: string,
    files: File[],
): TRequestPromise<IDocumentUploadFileResponse[]> => {
    const formData = new FormData();
    formData.append('sectionCode', sectionCode);
    formData.append('subsectionCode', subsectionCode);
    files.forEach((file, index) => formData.append(`files[${index}]`, file));

    return performRequest({
        method: ERequestType.POST,
        url: 'fileupload/save',
        data: formData,
    });
};

export const fetchDefaultSignInfo = (
    data: IDefaultSignInfoRequest,
): TRequestPromise<IDocumentUploadSignatureResponse[]> => {
    const formData = convertToFormData(data as Record<string, TJsonFormValue>);

    return performRequest({
        method: ERequestType.POST,
        url: 'fileupload/sign-attach-info/list',
        data: formData,
    });
};

export const runXmlAndProgramParser = async (
    id: string | number,
    {
        onParsingStart,
        onParsingSuccess,
        onParsingException,
    }: IParserRequestOptions,
) => {
    const runProgramParser = createParserRequest('/fileupload/runProgramParser');
    const runXmlParser = createParserRequest('/fileupload/runXmlParser');

    onParsingStart();

    try {
        const parsingResults = await Promise.all([
            runXmlParser(id),
            runProgramParser(id),
        ]);

        const successMessage = parsingResults
            .map(result => result.data.message)
            .join('. ');

        onParsingSuccess(successMessage);
    } catch (error) {
        const exceptionMessage = error?.response.data.message;
        onParsingException(exceptionMessage);
    }
};

export const uploadDocumentFileRequest = (
    data: Record<string, any>,
    {onFileUploadProgress, onFileUploadException}: IUploadFilesRequestOptions,
    params?: IUploadFilesRequestParams,
) => performRequest({
    method: ERequestType.POST,
    url: 'fileupload', // URL теперь всегда будет фиксированным
    onUploadProgress: onFileUploadProgress,
    data: convertToFormData(data),
    params,
})
    .catch(({response}: AxiosError) => {
        showMessageFromResponse({response, isError: true});
        onFileUploadException(response?.data?.message);
    });

export const uploadDocumentSignatureRequest = (
    data: Record<string, any>,
    {onFileUploadProgress, onFileUploadException, onFileUploadSuccess}: IUploadSignsRequestOptions,
) => performRequest({
    method: ERequestType.POST,
    url: 'fileupload/saveSgnFile',
    onUploadProgress: onFileUploadProgress,
    data: convertToFormData(data),
})
    .then((response: AxiosResponse) => {
        const successMessage = response.data?.message;
        onFileUploadSuccess(successMessage);
    })
    .catch(({response}: AxiosError) => {
        if (response?.status === 422) {
            showMessageFromResponse({response, isError: true});
        }
        onFileUploadException(response?.data.message);
    });

export const fetchFileUploadProperties = async (
    setDocumentsUploadConfig: Dispatch<SetStateAction<IDocumentsUploadConfig>>,
) => {
    await performRequest({
        method: ERequestType.GET,
        url: 'property/FILE_SIZE_UOM',
    }).then(data => {
        if (Object.values(EFileSizeFormat).includes(data?.data?.toUpperCase())) {
            setDocumentsUploadConfig(v => ({...v, fileSizeFormat: data.data}));
        } else {
            setDocumentsUploadConfig(v => ({...v, fileSizeFormat: null}));
        }
    }).catch(() => setDocumentsUploadConfig(v => ({...v, fileSizeFormat: null})));

    await performRequest({
        method: ERequestType.GET,
        url: 'property/UNACCEPTED_FILE_EXTENSIONS_FOR_UPLOADS',
    }).then(
        res => res.data && typeof res.data === 'string' && setDocumentsUploadConfig(
            v => ({...v, unacceptedFileExtensions: res.data.split(',')}),
        ),
    ).catch(() => setDocumentsUploadConfig(
        v => ({...v, unacceptedFileExtensions: undefined}),
    ));

    await performRequest({
        method: ERequestType.GET,
        url: 'property/NEED_START_DOC_DATE',
    }).then(res => res?.data && typeof res.data === 'string' && setDocumentsUploadConfig(
        v => ({...v, useUploadDate: res.data !== 'N'}),
    )).catch(() => setDocumentsUploadConfig(v => ({...v, useUploadDate: undefined})));

    await performRequest({
        method: ERequestType.GET,
        url: 'property/ALLOW_UNVERIFIED_FILE_UPLOAD_FLAG',
    }).then(res => res?.data && typeof res.data === 'string' && setDocumentsUploadConfig(
        v => ({...v, xsdValidationEnabled: res.data !== 'Y'}),
    )).catch(() => setDocumentsUploadConfig(v => ({...v, xsdValidationEnabled: undefined})));
};

export const fetchBranchNames = (): TRequestPromise<IDocumentTypeResponse[]> => performRequest({
    method: ERequestType.GET,
    url: 'valueLists/BRANCHES_COMPANIES',
});

export const fetchDocumentsSigningMethod = (): TRequestPromise<EDocumentsSigningMethod> => performRequest({
    method: ERequestType.GET,
    url: 'property/DOCUMENT_SIGNING_METHOD',
});

export const fetchFileAttachInfo = (
    sectionCode: string, subsectionCode: string, fileNames: string[],
): TRequestPromise<IFileAttachInfoResponse[]> => performRequest({
    method: ERequestType.POST,
    url: 'fileupload/attach-info',
    params: {sectionCode, subsectionCode},
    data: fileNames,
});

export const fetchFileDuplicatesInfo = (
    fileList: Pick<IDocumentUploadFileRequest, 'docTypeId' | 'fileUploadInterfaceId' | 'corrNum'>[],
): TRequestPromise<string[]> => performRequest({
    method: ERequestType.POST,
    url: 'fileupload/validateDuplicates',
    data: fileList,
});

export const fetchTemplateTypesForFile = async (lookupType: string
  | undefined): Promise<IDocumentTemplatesTypeResponse[]> => {
    try {
        const res = await performRequest({
            method: ERequestType.GET,
            url: 'valueLists/DOCUMENT_TYPES_TEMPLATES',
            params: {sublistCode: lookupType},
        });
        return res.data;
    } catch (e) {
        console.error('Невозможно получить значения по умолчанию для загружаемых файлов', e);
    }
    return [];
};

export const uploadFileToCache = async (
    {tableRow, sessionId}: {tableRow: IDocumentUploadFile; sessionId: string},
) => {
    const {filename, file} = await compressFile({filename: tableRow?.file?.name, file: tableRow?.file});

    const formData = new FormData();
    formData.append('fileContent', file, filename);
    formData.append('fileName', filename);
    formData.append('hashLoad', String(sessionId));

    const res = await performRequest({
        method: ERequestType.POST,
        url: 'fileupload.interface/upload',
        data: formData,
    });
    return res.data;
};
export const deleteCachedFilesById = async (id: string | number) => performRequest({
    method: ERequestType.DELETE,
    url: `fileupload.interface/delete/${id}`,
});

export const deleteCachedFilesByHashId = async (id: string) => performRequest({
    method: ERequestType.POST,
    url: 'fileupload.interface/delete-by-hash',
    params: {fileHash: id},
});

/**
 * Выполняет валидацию файла по XSD/XML-схеме для указанного типа документа.
 *
 * Эта функция отправляет id закешированного файла и код типа документа на сервер для выполнения валидации
 * (например, проверки структуры XML). Используется для получения статуса валидации файла
 * до загрузки в систему.
 *
 * @param {Object} params - Параметры для валидации
 * @param {string} params.docTypeId - Идентификатор типа документа
 * @param {string} params.docTypeCode - Код типа документа
 * @param {string} params.fileUploadInterfaceId - Идентификатор файла в кэше
 *
 * @returns {TRequestPromise<ValidatedFileResponse>}
 * Возвращает промис с объектом ответа от сервера, содержащим результат валидации:
 *  - `validationResult.statusCode` — код результата валидации.
 *  - `validationResult.meaning` — текстовое описание статуса.
 *  - `validationResult.description` — подробное описание или лог ошибок (если есть).
 *  - `lookupCode` — дополнительный код статуса XML.
 *
 * @throws {AxiosError} В случае сетевой ошибки или ошибки валидации с сервера.
 */
export const fetchFileValidation = (
    {docTypeId, docTypeCode, fileUploadInterfaceId}: {
        docTypeId: string;
        docTypeCode?: string;
        fileUploadInterfaceId: string;
    },
): TRequestPromise<IFileValidationResponse> => {
    const formData = new FormData();
    formData.append('docTypeId', docTypeId);
    formData.append('docTypeCode', docTypeCode ?? 'OTHER');

    if (fileUploadInterfaceId) {
        formData.append('fileUploadInterfaceId', fileUploadInterfaceId);
    }

    return performRequest<IFileValidationResponse>({
        method: ERequestType.POST,
        url: 'fileupload/validateStatus',
        data: formData,
    });
};

/**
 * Валидирует файл с возможностью перезагрузки в кэш если файл не найден в кэше.
 * Если валидация не проходит из-за отсутствия файла в кэше, функция автоматически
 * перезагружает файл в кэш и повторяет валидацию.
 *
 * @param {FormData} formDataForValidation - FormData с параметрами для валидации.
 * @param {Blob} file - Файл для валидации.
 * @param {string} filename - Имя файла.
 * @param {string} sessionId - Идентификатор сессии.
 * @returns {Promise<{data: IFileValidationResponse, newFileUploadInterfaceId:string | null}>} - Результат валидации
 * и новый идентификатор файла в кэше.
 */
const validateStatusWithReuploadCache = async (
    validationPayload: {
        docTypeId: string;
        docTypeCode?: string;
        fileUploadInterfaceId: string;
    },
    tableRow: IDocumentUploadFile,
    sessionId: string,
) => {
    try {
        const data = await fetchFileValidation(validationPayload);
        return {data, newFileUploadInterfaceId: null};
    } catch (error) {
        const isNotFound400 = error?.response?.status === 400
                && typeof error?.response?.data === 'string'
                && error?.response?.data.includes('не найдена');
        const isFileUploadInterfaceIdMissing = !validationPayload?.fileUploadInterfaceId;

        // Если файл не найден в кэше — перезагружаем файл в кэш и повторяем валидацию
        if (isNotFound400 || isFileUploadInterfaceIdMissing) {
            const uploadedFileRes = await uploadFileToCache({
                tableRow,
                sessionId,
            });
            const newFileUploadInterfaceId = String(uploadedFileRes?.id);

            const data = await fetchFileValidation(
                {...validationPayload, fileUploadInterfaceId: newFileUploadInterfaceId},
            );
            return {data, newFileUploadInterfaceId};
        }

        throw error;
    }
};

/**
 * Валидирует файл документа и обновляет идентификатор файла в интерфейсе загрузки.
 *
 * @param {Object} params - Параметры валидации
 * @param {Blob} params.fileBlob - Blob объект файла для валидации
 * @param {IDocumentUploadFile} params.tableRow - Данные строки таблицы с информацией о документе
 * @param {string | null} params.fileUploadInterfaceId - Идентификатор файла в интерфейсе загрузки
 * @param {string} params.sessionId - Идентификатор сессии
 * @returns {Promise<{validationData: IFileValidationResponse | null, updatedFileUploadInterfaceId: string | null}>}
 * Результат валидации и обновленный идентификатор файла
 */
export const handleValidateFile = async ({
    tableRow,
    fileUploadInterfaceId,
    sessionId,
}: {
    tableRow: IDocumentUploadFile;
    fileUploadInterfaceId: string | null;
    sessionId: string;
}): Promise<{
    validationData: Partial<IFileValidationResponse> | null;
    updatedFileUploadInterfaceId: string | null;
}> => {
    let validationResponse = null;
    let updatedFileUploadInterfaceId = fileUploadInterfaceId;

    try {
        const validationPayload = {
            docTypeId: String(tableRow?.type),
            docTypeCode: String(tableRow?.typeCode),
            fileUploadInterfaceId: String(fileUploadInterfaceId ?? ''),
        };

        const {data, newFileUploadInterfaceId} = await validateStatusWithReuploadCache(
            validationPayload,
            tableRow,
            sessionId,
        );
        validationResponse = data;

        if (newFileUploadInterfaceId) {
            updatedFileUploadInterfaceId = newFileUploadInterfaceId;
        }
    } catch (error) {
        console.error('Ошибка валидации файла:', error);
    }

    return {
        validationData: validationResponse?.data ?? null,
        updatedFileUploadInterfaceId,
    };
};

// ==== Common requests ====
function createParserRequest(url: string) {
    return (id: string | number) => performRequest({
        method: ERequestType.POST,
        url: `${url}/${id}`,
    });
}

export const getFinalStatus = (
    originalStatusCode: string,
    actionCode: string,
    entityName: string,
): TRequestPromise<string> => performRequest({
    method: ERequestType.GET,
    url: 'administration.status.document/get-final-status',
    params: {originalStatusCode, actionCode, entityName},
});

/**
 * Универсальный геттер для GET-запросов с docTypeId.
 * @param url - endpoint типа get-timestamp / get-corrnum / get-regnum
 * @param docTypeId - идентификатор типа документа
 */
export const fetchDocTypeFlag = (url: string, docTypeId: string | number) => performRequest({
    method: ERequestType.GET,
    url: `directories.type.documents/rpc/${url}`,
    params: {docTypeC: docTypeId},
});

// ==== Params types for requests ====
export interface IFetchDocumentsForUploadAttachArgs {
    sublistCode: string;
}

interface IDownloadDocumentScanArgs {
    attachmentId: string;
    fileName?: string;
}

interface IDownloadScansArchiveByDocumentIdArgs {
    docId: string;
    fileName?: string;
}

interface IFetchDocumentScansByIdArgs {
    docId: string;
}

interface IDeleteDocumentScanArgs {
    attachmentId: string;
}

interface IDeleteDocumentScansByDocumentIdArgs {
    docId: string;
}

interface IUploadAttachedArgs {
    file: File;
    docId: string;
    handleProgress?: (progressEvent: ProgressEvent) => void;
}

interface IDocumentInfoParams extends IContextParamType {
    sgnFileName?: string;
    entityName?: string;
}

interface IContextParamType {
    organizationId?: number;
    taxPeriodId?: number;
    taxTypeId?: number;
}

interface IDocumentForSignType extends IContextParamType {
    entityName?: string;
}

interface IDefaultSignInfoRequest extends IContextParamType {
    entityName?: string;
    files?: File[];
}

interface IUploadFilesRequestParams {
    organizationId?: number;
    taxPeriodId?: number;
    userId?: number;
    docUploadTemplateCode?: string;
}

export interface IParserRequestOptions {
    onParsingStart: () => void;
    onParsingSuccess: (message?: string) => void;
    onParsingException: (message?: string) => void;
}

export interface IUploadFilesRequestOptions extends IParserRequestOptions {
    onFileUploadProgress: (progressEvent: any) => void;
    onFileUploadException: (message?: string) => void;
    onFileUploadSuccess: (message?: string) => void;
    shouldUseFileLoadStatus?: boolean;
}

export interface IUploadSignsRequestOptions {
    onFileUploadProgress: (progressEvent: any) => void;
    onFileUploadException: (message?: string) => void;
    onFileUploadSuccess: (message?: string) => void;
}
